import { formatUZS } from "@elektr/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Field, Input } from "@/components/ui/form";
import { useToast } from "@/components/ui/toast";
import { ApiError, api, errorMessage, unwrap } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const CHOICES = [20, 50, 100, 250] as const;
// Kalitlar literal: i18n qamrov testi dinamik kalitlarni ko'rmaydi
const BLOCKED = {
  no_provider: "server.expand_blocked_no_provider",
  no_agent: "server.expand_blocked_no_agent",
  unknown_capacity: "server.expand_blocked_unknown_capacity",
  too_big: "server.expand_blocked_too_big",
  active: "server.expand_blocked_active",
  daily_limit: "server.expand_blocked_daily_limit",
} as const;
const ERRORS = {
  bad_password: "server.expand_err_bad_password",
  rate_limited: "server.expand_err_rate_limited",
  ...BLOCKED,
} as const;
type ErrorCode = keyof typeof ERRORS;

export function ExpandDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { t } = useT();
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={t("server.expand_title")}>
      {open && <ExpandForm onClose={() => onOpenChange(false)} />}
    </Dialog>
  );
}

function ExpandForm({ onClose }: { onClose: () => void }) {
  const { t } = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const [addGb, setAddGb] = useState<number>(50);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const preview = useQuery({
    queryKey: ["hosting", "expand-preview", addGb],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/hosting/expand/preview/", { params: { query: { add_gb: addGb } } }),
      ),
  });
  const start = useMutation({
    mutationFn: async () =>
      unwrap(await api.POST("/api/hosting/expand/", { body: { add_gb: addGb, password } })),
    onSuccess: () => {
      toast.success(t("server.expand_started"));
      void qc.invalidateQueries({ queryKey: ["hosting"] });
      onClose();
    },
    onError: (e) => {
      const code =
        e instanceof ApiError && e.body && typeof e.body === "object"
          ? (e.body as { code?: string }).code
          : undefined;
      setError(
        code && code in ERRORS ? t(ERRORS[code as ErrorCode]) : errorMessage(e, t("common.error")),
      );
      setPassword("");
    },
  });

  const p = preview.data;
  const ready = !!p?.can_expand && password.length > 0 && !start.isPending;
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        if (ready) start.mutate();
      }}
    >
      <div>
        <div className="mb-2 text-sm font-medium" id="expand-choose">
          {t("server.expand_choose")}
        </div>
        <div role="group" aria-labelledby="expand-choose" className="flex flex-wrap gap-2">
          {CHOICES.map((n) => (
            <button
              key={n}
              type="button"
              aria-pressed={addGb === n}
              onClick={() => setAddGb(n)}
              className={cn(
                "min-h-11 rounded-lg border px-4 text-sm font-semibold",
                addGb === n
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-card hover:bg-muted",
              )}
            >
              +{n} GB
            </button>
          ))}
        </div>
      </div>

      <div
        className="rounded-lg bg-muted p-3 text-sm"
        aria-live="polite"
        data-testid="expand-preview"
      >
        {p && (
          <>
            {p.from_gb != null && (
              <div className="font-semibold">
                {t("server.expand_from_to", { from: String(p.from_gb), to: String(p.to_gb) })}
              </div>
            )}
            {p.current_monthly_uzs != null && p.new_monthly_uzs != null ? (
              <div>
                {t("server.expand_cost", {
                  from: formatUZS(p.current_monthly_uzs),
                  to: formatUZS(p.new_monthly_uzs),
                })}
              </div>
            ) : (
              <div className="text-warning">{t("server.expand_cost_unknown")}</div>
            )}
            {p.months != null && (
              <div className="text-muted-foreground">
                {t("server.expand_months", { n: String(p.months).replace(".", ",") })}
              </div>
            )}
            {p.blocked_reason && (
              <div role="alert" className="mt-1 font-semibold text-danger">
                {t(BLOCKED[p.blocked_reason])}
              </div>
            )}
          </>
        )}
        <p className="mt-2 text-xs text-muted-foreground">{t("server.expand_note")}</p>
      </div>

      <Field label={t("server.expand_password")} error={error}>
        {(id) => (
          <Input
            id={id}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        )}
      </Field>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button type="submit" disabled={!ready}>
          {t("server.expand_submit")} +{addGb} GB
        </Button>
      </div>
    </form>
  );
}
